﻿import { convertSecToTime } from "@shared/Utils";

interface VideoInfo {
  duration: string;
}

interface VideoOverlayData {
  overlay: HTMLElement;
  videoInfo?: VideoInfo;
  videoElement: HTMLVideoElement;
  popupBox: HTMLElement;
  popupBoxName: string;
}

class VideoPopupDialog {
  swipeIsStarted = false;
  MouseMoveIsStarted = false;
  eventLock: string[] = [];
  videoStoppedClassName = "js-video-stopped";
  idswithObserver: string[] = [];
  observer: IntersectionObserver = null;

  constructor() {
    const options = {
      rootMargin: "0px",
      threshold: [0, 0.3, 0.5],
    };
    this.observer = new IntersectionObserver(this.observerCallBack(), options);
    const popupClickItem = document.querySelectorAll<HTMLElement>(".js-open-popup-modal");
    popupClickItem.forEach((overlay) => this.initOverlay(overlay));

    this.updateEvents();
  }

  initOverlay(overlay: HTMLElement): void {
    const popupBoxName = overlay.getAttribute("modal-box");
    const popupBox = this.getPopupBox(popupBoxName);
    const videoUrl = overlay.getAttribute("video-url");
    const videoElement = popupBox.querySelector<HTMLVideoElement>("video");
    videoElement.src = videoUrl;

    const videoData: VideoOverlayData = {
      videoElement: videoElement,
      overlay: overlay,
      popupBox: popupBox,
      popupBoxName: popupBoxName,
    };

    this.getVideoMeta(videoElement)
      .then((info) => {
        videoData.videoInfo = info;
        this.appendDurationView(videoData);
      })
      .catch((e) => {
        console.error(e);
      });

    this.addObserver(overlay);
    this.addOnClickEvent(videoData);
    this.addKeyboardEvents(videoData);
  }

  appendDurationView(videoElement: VideoOverlayData): void {
    const textEl = videoElement.overlay.querySelector(".po-text");

    if (videoElement.videoInfo && videoElement.videoInfo.duration) {
      const el = document.createElement("div");
      el.classList.add("po-duration");
      el.innerHTML = `(${videoElement.videoInfo.duration})`;
      textEl.append(el);
    }
    textEl.classList.add("show");
  }

  public updateEvents(): void {
    const popupBox = this.getPopupBox("video-popup-modal");
    const videoElement = popupBox.querySelector<HTMLVideoElement>("video");

    popupBox.addEventListener("onModalDialogClosed", () => {
      videoElement.pause();
    });
  }

  /*
   eventIsLocked prevents race condition between events
  */
  eventIsLocked(item: HTMLElement): boolean {
    if (this.eventLock.includes(item.id)) {
      return true;
    }

    this.eventLock.push(item.id);
    const id = item.id;

    setTimeout(() => {
      this.eventLock = this.eventLock.filter((x) => x !== id);
    }, 100);

    return false;
  }

  addObserver(overlay: HTMLElement): void {
    const previewVideo = overlay.parentNode.querySelector<HTMLVideoElement>(".video-container video");

    if (previewVideo) {
      this.getVideoMeta(previewVideo)
        .then(() => {
          this.observer.unobserve(previewVideo);
          this.observer.observe(previewVideo);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  observerCallBack(): IntersectionObserverCallback {
    const stoppedClassName = this.videoStoppedClassName;
    return (entries: Array<IntersectionObserverEntry>): void => {
      entries.forEach((entry) => {
        const videoItem = entry.target as HTMLVideoElement;

        if (entry.intersectionRatio >= 0.3 && !videoItem.classList.contains(stoppedClassName)) {
          videoItem.play().catch((e) => {
            console.error(e);
          });
        } else {
          videoItem.pause();
        }

        if (entry.intersectionRatio < 0.3) {
          videoItem.classList.remove(stoppedClassName);
        }
      });
    };
  }

  getPopupBox(popupBoxName: string): HTMLElement {
    return document.querySelector<HTMLElement>(`.${popupBoxName}`);
  }

  sendPopupEvent(popupBoxName: string): void {
    const eventToBeDispatched = new CustomEvent("__ice_openModal", {
      detail: {
        dialogBoxlayout: popupBoxName,
      },
    });
    document.dispatchEvent(eventToBeDispatched);
  }

  addKeyboardEvents(videoData: VideoOverlayData): void {
    videoData.overlay.addEventListener("keydown", (e: KeyboardEvent) => {
      if (this.eventIsLocked(videoData.overlay)) {
        return;
      }

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          this.setUpVideo(videoData);
          break;
        case " ":
          e.preventDefault();
          this.setUpVideo(videoData);
          break;
      }
    });
  }

  addOnClickEvent(videoData: VideoOverlayData): void {
    videoData.overlay.addEventListener("click", () => {
      if (this.eventIsLocked(videoData.overlay)) {
        return;
      }

      this.setUpVideo(videoData);
    });
  }

  setUpVideo(videoData: VideoOverlayData): void {
    const previewVideoItem = videoData.overlay.parentNode.querySelector<HTMLVideoElement>(".video-container video");

    this.sendPopupEvent(videoData.popupBoxName);
    videoData.videoElement.play().catch((e) => {
      console.error(e);
    });

    if (previewVideoItem) {
      previewVideoItem.classList.add(this.videoStoppedClassName);
      previewVideoItem.pause();
    }
  }

  getVideoMeta(elVideo: HTMLVideoElement): Promise<VideoInfo> {
    return new Promise((resolve): void => {
      elVideo.addEventListener(
        "loadedmetadata",
        function () {
          const videoInfo: VideoInfo = {
            duration: convertSecToTime(this.duration),
          };
          resolve(videoInfo);
        },
        false,
      );
    });
  }
}

document.addEventListener("DOMContentLoaded", (): void => {
  new VideoPopupDialog();
});
